import { useState } from "react";
import { Link } from "react-router-dom";

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  backgroundColor: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "10px",
  color: "#E7EBEF",
  fontSize: "14px",
  outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
  WebkitBoxShadow: "0 0 0 1000px rgba(13,17,23,0.95) inset",
  WebkitTextFillColor: "#E7EBEF",
};

const inputFocusStyle = {
  borderColor: "#30A497",
  boxShadow: "0 0 0 2px rgba(48,164,151,0.2)",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 500,
  color: "#94A3B8",
  marginBottom: "6px",
  letterSpacing: "0.03em",
  textTransform: "uppercase",
};

const FocusableInput = ({ id, name, type, placeholder, required, extraStyle }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      id={id}
      name={name}
      type={type}
      required={required}
      placeholder={placeholder}
      style={{
        ...inputStyle,
        ...(focused ? inputFocusStyle : {}),
        ...extraStyle,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const LoginFormInputFields = ({
  handleSignIn,
  isFetching,
  goForgotMyPassword,
  error,
  handleError,
  handleGoogleSignIn,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [pwFocused, setPwFocused] = useState(false);

  return (
    <div className="space-y-4">
      <form className="space-y-4" onSubmit={handleSignIn}>
        {/* Email */}
        <div>
          <label htmlFor="email" style={labelStyle}>Email</label>
          <FocusableInput
            id="email"
            name="email"
            type="email"
            required
            placeholder="doctor@hospital.com"
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" style={labelStyle}>Password</label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              required
              placeholder="••••••••"
              style={{
                ...inputStyle,
                paddingRight: "44px",
                ...(pwFocused ? inputFocusStyle : {}),
              }}
              onFocus={() => setPwFocused(true)}
              onBlur={() => setPwFocused(false)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors duration-200"
              style={{ color: "#64748B" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#94A3B8")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#64748B")}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Forgot password */}
        <div className="flex justify-end">
          <button
            type="button"
            className="text-sm font-medium transition-colors duration-200"
            style={{ color: "#94A3B8" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#30A69C")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#94A3B8")}
            onClick={goForgotMyPassword}
          >
            Forgot password?
          </button>
        </div>

        {/* Error */}
        {error && handleError && (
          <div
            className="rounded-lg p-3"
            style={{
              backgroundColor: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.25)",
            }}
          >
            <p className="text-red-400 text-sm">{handleError}</p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isFetching}
          className="w-full flex items-center justify-center font-semibold transition-all duration-200 disabled:opacity-50 rounded-xl py-3 px-4"
          style={{
            background: "linear-gradient(160deg, #35b5aa 0%, #249990 100%)",
            color: "#fff",
            border: "none",
            boxShadow: "0 4px 14px rgba(48,164,151,0.4)",
            cursor: isFetching ? "not-allowed" : "pointer",
          }}
          onMouseEnter={(e) => {
            if (!isFetching) e.currentTarget.style.background = "linear-gradient(160deg, #249990 0%, #1a7068 100%)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "linear-gradient(160deg, #35b5aa 0%, #249990 100%)";
          }}
        >
          {isFetching ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Logging in...</span>
            </>
          ) : (
            "Log In"
          )}
        </button>
      </form>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div style={{ flex: 1, height: "1px", backgroundColor: "rgba(255,255,255,0.1)" }} />
        <span className="text-xs" style={{ color: "#64748B" }}>or</span>
        <div style={{ flex: 1, height: "1px", backgroundColor: "rgba(255,255,255,0.1)" }} />
      </div>

      {/* Google */}
      <button
        onClick={handleGoogleSignIn}
        disabled={isFetching}
        className="w-full flex items-center justify-center font-medium transition-all duration-200 disabled:opacity-50 rounded-xl py-3 px-4"
        style={{
          backgroundColor: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.12)",
          color: "#E7EBEF",
          cursor: isFetching ? "not-allowed" : "pointer",
        }}
        onMouseEnter={(e) => {
          if (!isFetching) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.09)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
        }}
      >
        <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        <span>Continue with Google</span>
      </button>

      {/* Sign up link */}
      <div className="text-center flex items-center justify-center space-x-2 mt-2">
        <p className="text-sm" style={{ color: "#64748B" }}>Don't have an account?</p>
        <Link
          to="/signup"
          className="font-semibold text-sm transition-colors duration-200"
          style={{ color: "#30A497", textDecoration: "none" }}
          onMouseOver={(e) => (e.currentTarget.style.color = "#50C6BC")}
          onMouseOut={(e) => (e.currentTarget.style.color = "#30A497")}
        >
          Sign Up
        </Link>
      </div>
    </div>
  );
};

export default LoginFormInputFields;
